import PropTypes from 'prop-types'
import { isNil } from 'ramda'
import ActiveRecord from 'model/activeRecord'
import ContactsV2 from 'model/provider/contactsV2'
import DoctorSpecialty from 'model/provider/doctorSpecialty'
import SPECIALTIES_SEARCH from 'apollo/queries/SPECIALTIES_SEARCH'
import { getNodes } from 'utils/paginationHelper'
import client from 'apollo/client'

const PROVIDER_TYPE = 'DoctorProfileV2'

class Provider extends ActiveRecord {
  constructor(init) {
    super(init)
    if (isNil(init)) return undefined
    this._isProvider = init.isProvider
    this._addresses = init.addresses
      ? init.addresses.map(item => new ContactsV2(item))
      : []
    this._contacts = new ContactsV2(init.contacts)
    this._awards = init.awards
    this._bio = init.bio
    this._insurance_tier = init.insurance_tier
    this._certifications = init.certifications
    this._conditions = init.conditions
    this._confirmed = init.confirmed
    this._confirmed_at = init.confirmed_at
    this._confirmed_by = init.confirmed_by
    this._educations = init.educations
    this._educations_details = init.educations_details
    this._full_name = init.full_name
    this._gender = init.gender
    this._high_patient_satisfaction = init.high_patient_satisfaction
    this._high_quality_provider = init.high_quality_provider
    this._high_quality_practice_location = init.high_quality_practice_location
    this._low_cost_provider = init.low_cost_provider
    this._id = init.id
    this._image_url = init.image_url
    this._images = init.images // NOT IN DB, this is for chat gpt data plug only
    this._internal_notes = init.internal_notes
    this._memberships = init.memberships
    this._npi_id = init.npi_id
    this._online_profiles = init.online_profiles
    this._procedures = init.procedures
    this._propublica = init.propublica
    this._rating = init.rating
    this._source = init.source
    this._specialties = init.specialties
      ? init.specialties.map(item => new DoctorSpecialty(item))
      : []
    this._video_urls = init.video_urls
    this._specialty_names = init.specialty_names
    this._ratings_count = init.ratings_count
  }

  static shape = {
    addresses: PropTypes.arrayOf(PropTypes.instanceOf(ContactsV2)),
    awards: PropTypes.array,
    bio: PropTypes.string,
    insurance_tier: PropTypes.object,
    certifications: PropTypes.array,
    conditions: PropTypes.array,
    confirmed: PropTypes.bool,
    confirmed_at: PropTypes.string,
    confirmed_by: PropTypes.object,
    educations: PropTypes.array,
    educations_details: PropTypes.array,
    full_name: PropTypes.string,
    gender: PropTypes.string,
    high_patient_satisfaction: PropTypes.bool,
    high_quality_provider: PropTypes.bool,
    high_quality_practice_location: PropTypes.bool,
    low_cost_provider: PropTypes.bool,
    id: PropTypes.string,
    image_url: PropTypes.string,
    internal_notes: PropTypes.string,
    npi_id: PropTypes.string,
    memberships: PropTypes.array,
    online_profiles: PropTypes.array,
    procedures: PropTypes.array,
    propublica: PropTypes.object,
    rating: PropTypes.number,
    source: PropTypes.string,
    specialties: PropTypes.arrayOf(PropTypes.instanceOf(DoctorSpecialty)),
    video_urls: PropTypes.array,
    specialty_names: PropTypes.array,
    ratings_count: PropTypes.object
  }

  // Mutations
  static getSpecialtyName(rawType) {
    const result = client.readQuery({
      query: SPECIALTIES_SEARCH
    })
    const specialties = getNodes(['specialties'])(result)

    if (specialties) {
      const item = specialties.find(i => i.uuid === rawType)
      return item?.provider_name
    }
    return rawType
  }

  // Getters for class fields
  get addresses() {
    return this._addresses || []
  }

  get awards() {
    return this._awards || []
  }

  get bio() {
    return this._bio || null
  }

  get insurance_tier() {
    return this._insurance_tier
  }

  get certifications() {
    return this._certifications || []
  }

  get conditions() {
    return this._conditions || []
  }

  get confirmed() {
    return this._confirmed || false
  }

  get confirmed_at() {
    return this._confirmed_at || null
  }

  get confirmed_by() {
    return this._confirmed_by || {}
  }

  get educations() {
    return this._educations || []
  }

  get educations_details() {
    return this._educations_details || []
  }

  get full_name() {
    return this._full_name || ''
  }

  get gender() {
    return this._gender
  }

  get high_patient_satisfaction() {
    return this._high_patient_satisfaction
  }

  get high_quality_provider() {
    return this._high_quality_provider
  }

  get high_quality_practice_location() {
    return this._high_quality_practice_location
  }

  get low_cost_provider() {
    return this._low_cost_provider
  }

  get id() {
    return this._id || null
  }

  get image_url() {
    return this._image_url || ''
  }

  get images() {
    return this._images || []
  }

  get internal_notes() {
    return this._internal_notes || null
  }

  get memberships() {
    return this._memberships || []
  }

  get npi_id() {
    return this._npi_id || ''
  }

  get online_profiles() {
    if (!this._online_profiles || !Array.isArray(this._online_profiles))
      return []

    return this._online_profiles.map(i => i.url)
  }

  get procedures() {
    return this._procedures || []
  }

  get propublica() {
    return this._propublica || {}
  }

  get rating() {
    return this._rating
  }

  get specialties() {
    return this._specialties || []
  }

  get video_urls() {
    return this._video_urls || []
  }

  get source() {
    return this._source || ''
  }

  get specialty_names() {
    return this._specialty_names
  }

  get ratings_count() {
    return this._ratings_count
  }

  // Calculable
  get isProvider() {
    return this._isProvider || this.__typename === PROVIDER_TYPE
  }

  get getDisplayName() {
    return this.full_name
  }

  get getClosestAddress() {
    let closestLocation = false
    let closestDistance = Number.POSITIVE_INFINITY

    const { addresses } = this
    for (let i = 0; i < addresses.length; i++) {
      if (addresses[i].distance < closestDistance) {
        closestDistance = addresses[i].distance
        closestLocation = addresses[i]
      }
    }

    const primaryAddress = this.addresses.find(i => i?.rightway_primary)

    return closestLocation || primaryAddress || addresses[0] || false
  }

  get getSpecialtiesUids() {
    const { specialties } = this
    return specialties.map(i => i.uid)
  }
}

export default Provider
